import {
  ANALYTICS_APPS,
  WORKSPACE_GROUP_TYPE,
  type AnalyticsEventName,
  type AnalyticsGroupProperties,
  type AnalyticsPersonProperties,
  type PropertiesFor,
} from '@syncanix/shared';
import posthog from 'posthog-js';

import { logger } from '@/lib/logger';

/**
 * The dashboard's thin, typed wrapper over `posthog-js`. Every public method is
 * a NO-OP until `initAnalytics` succeeds, so:
 *
 *   - local dev (no `VITE_POSTHOG_KEY`) never ships an event, and
 *   - call sites never need a "is analytics on?" guard — they just call.
 *
 * Privacy posture (see docs/decisions/0077-product-analytics-posthog.md):
 *   - `autocapture` OFF — we send explicit, named events only; no scraping of
 *     DOM text (which can contain customer data).
 *   - `disable_session_recording` ON — no replay of an authenticated console.
 *   - `person_profiles: 'identified_only'` — anonymous visitors get no profile;
 *     a profile is created only once we `identify` by the Auth0 `sub`.
 *   - pageviews are sent MANUALLY with the search string stripped, so a token
 *     that ever lands in a query param can't be exfiltrated to analytics.
 */

/** EU cloud by default — data-residency aligned with the eu-central-1 posture. */
const DEFAULT_API_HOST = 'https://eu.i.posthog.com';

let initialized = false;

export interface InitAnalyticsConfig {
  /** PostHog project API key (`phc_…`). Absent in local dev → analytics no-ops. */
  readonly apiKey?: string | undefined;
  /** Ingestion host override. Defaults to the EU cloud. */
  readonly apiHost?: string | undefined;
  /** Deploy environment super-property: `development` | `production`. */
  readonly environment: string;
  /** Active UI locale super-property. */
  readonly locale: string;
}

/**
 * Initialise PostHog exactly once. Returns whether analytics is live (i.e. a
 * key was provided and init ran). Safe to call repeatedly — subsequent calls
 * are ignored.
 */
export function initAnalytics(config: InitAnalyticsConfig): boolean {
  if (initialized) return true;
  if (!config.apiKey) return false;

  posthog.init(config.apiKey, {
    api_host: config.apiHost ?? DEFAULT_API_HOST,
    person_profiles: 'identified_only',
    autocapture: false,
    capture_pageview: false,
    capture_pageleave: true,
    disable_session_recording: true,
    persistence: 'localStorage+cookie',
  });
  posthog.register({
    app: ANALYTICS_APPS.DASHBOARD,
    environment: config.environment,
    locale: config.locale,
  });
  initialized = true;
  logger.info('[analytics] posthog initialised', { environment: config.environment });
  return true;
}

/** Strip query string + hash so a stray token in a param never reaches PostHog. */
function cleanUrl(pathname: string): string {
  if (typeof window === 'undefined') return pathname;
  return window.location.origin + pathname;
}

export const analytics = {
  /** Whether PostHog is live (false in local dev / before init). */
  isEnabled(): boolean {
    return initialized;
  },

  /**
   * Capture a typed product event. No-op until initialised.
   *
   * Pass `{ sendInstantly: true }` for an event fired immediately before a
   * navigation that unloads the page (e.g. the `/signin` → Auth0 redirect):
   * it bypasses PostHog's batch timer so the event isn't dropped on unload.
   */
  capture<N extends AnalyticsEventName>(
    name: N,
    properties?: PropertiesFor<N>,
    options?: { readonly sendInstantly?: boolean },
  ): void {
    if (!initialized) return;
    if (options?.sendInstantly) {
      posthog.capture(name, properties, { send_instantly: true });
      return;
    }
    posthog.capture(name, properties);
  },

  /** Send a manual `$pageview` with the query string stripped. */
  capturePageview(pathname: string): void {
    if (!initialized) return;
    posthog.capture('$pageview', { $current_url: cleanUrl(pathname), $pathname: pathname });
  },

  /** Update the active-locale super-property after a language switch. */
  setLocale(locale: string): void {
    if (!initialized) return;
    posthog.register({ locale });
  },

  /**
   * Bind the current person to the Auth0 `sub` (pseudonymous — no raw email).
   * Enriches with non-PII person properties only.
   */
  identify(distinctId: string, personProperties?: AnalyticsPersonProperties): void {
    if (!initialized) return;
    posthog.identify(distinctId, personProperties);
  },

  /** Associate subsequent events with the active workspace group (B2B analytics). */
  groupWorkspace(workspaceId: string, properties?: AnalyticsGroupProperties): void {
    if (!initialized) return;
    posthog.group(WORKSPACE_GROUP_TYPE, workspaceId, properties);
  },

  /** Clear identity on sign-out so the next user starts a fresh anonymous id. */
  reset(): void {
    if (!initialized) return;
    posthog.reset();
  },
};

export type Analytics = typeof analytics;
